import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps,
  IconButton,
  Typography,
  Box,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import Button from './Button'

export interface ModalProps extends Omit<DialogProps, 'onClose'> {
  title?: string
  onClose: () => void
  onConfirm?: () => void
  confirmText?: string
  cancelText?: string
  loading?: boolean
  size?: 'small' | 'medium' | 'large' | 'fullscreen'
  showCloseButton?: boolean
  showActions?: boolean
}

const Modal = ({
  title,
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  size = 'medium',
  showCloseButton = true,
  showActions = true,
  children,
  ...props
}: ModalProps) => {
  const getMaxWidth = () => {
    switch (size) {
      case 'small':
        return 'sm'
      case 'medium':
        return 'md'
      case 'large':
        return 'lg'
      case 'fullscreen':
        return false
      default:
        return 'md'
    }
  }

  const getFullScreen = () => size === 'fullscreen'

  return (
    <Dialog
      maxWidth={getMaxWidth()}
      fullWidth
      fullScreen={getFullScreen()}
      onClose={onClose}
      {...props}
    >
      {title && (
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            {showCloseButton && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      )}
      
      <DialogContent dividers>
        {children}
      </DialogContent>
      
      {showActions && (
        <DialogActions>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          {onConfirm && (
            <Button
              variant="primary"
              onClick={onConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default Modal
